import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import swal from "sweetalert";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Image as ImageLucide,
  ImageIcon,
  LayoutGrid,
  Lock,
  List as ListIcon,
  Plus,
  Search,
  Sparkles,
  Trash2,
  Upload,
  FileText,
} from "lucide-react";

import Spinner from "../../Layouts/Spinner";
import FormField from "../../Common/FormField/FormField";
import { handleImageUpload } from "../../Common/ImageUpload/handleImageUpload";
import { nbSelectStyles } from "../../Common/FormShell/nbSelectStyles";
import { addcategary } from "../../Constant/constant";
import { updateCategory } from "../../../redux/category/action";
import UnsavedChangesGuard from "./sections/UnsavedChangesGuard";
import {
  CATEGORY_STATUS,
  POSITION_MAX,
  POSITION_MIN,
} from "./constants";
import LandingPageSeoSection from "../../Common/LandingPageSeo/LandingPageSeoSection";
import PagePreview from "../../Common/LandingPageSeo/PagePreview";
import { crumbPath, metaDescriptionFallback, pageAddress } from "../../Common/LandingPageSeo/seoPreview";
import { landingPageValues, splitLandingPageValues } from "../../Common/LandingPageSeo/landingPageFields";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import axiosInstance from "../../../redux/api";
import "../../Common/FormShell/FormShell.css";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_SIZE_MB = 5;

const PREVIEW_TABS = [
  { key: "storefront", label: "Storefront", Icon: LayoutGrid },
  { key: "page", label: "Page", Icon: FileText },
  { key: "listing", label: "Admin list", Icon: ListIcon },
  { key: "search", label: "Search", Icon: Search },
];

const BANNER_TYPE_OPTIONS = [
  { value: 1, label: "Category" },
  { value: 2, label: "Occasion" },
  { value: 3, label: "Brand" },
];

const INITIAL_VALUES = {
  title: "",
  ar_title: "",
  image: "",
  // The SEO section edits the category's storefront page; see landingPageFields.
  ...landingPageValues(null),
  status: CATEGORY_STATUS.ACTIVE,
  position: 1,
  home_view: true,
  banner_image: "",
  ar_banner_image: "",
  banner_type: "",
  banner_master_id: "",
};

const slugify = (str = "") =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const normalizeBannerInt = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  if (typeof value === "number" && !Number.isNaN(value)) return value;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const emptyStrToNull = (value) => (value === "" ? null : value);

const CategoryForm = ({ mode = "create", embeddedId, onDone, onCancel }) => {
  const isEdit = mode === "edit";
  const { id: routeId } = useParams();
  const id = embeddedId ?? routeId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  // When hosted in the Catalog workbench drawer, save/cancel resolve to the
  // provided callbacks instead of navigating away to the list route.
  const embedded = typeof onDone === "function";
  // For redux actions that navigate internally on success, pass this shim so
  // embedded saves close the drawer instead of changing routes.
  const saveNav = embedded ? () => onDone() : navigate;
  const finishSave = () => (embedded ? onDone() : navigate("/category"));
  const finishCancel = () => (embedded ? onCancel?.() : navigate("/category"));
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const [submitting, setSubmitting] = useState(false);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewTab, setPreviewTab] = useState("storefront");
  const fileInputRef = useRef(null);

  // Banner-only dropdown options (edit mode only)
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [occasionOptions, setOccasionOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);

  // The category record is fetched into LOCAL state (not the shared Redux
  // singleton) so opening different categories in the drawer can't race or
  // show a stale record. `record` is null until this id's data arrives.
  const [record, setRecord] = useState(null);

  const isEditable = isEdit
    ? hasAction(MENU.CATALOGUE, PERMISSION.EDIT)
    : true;
  const readOnly = isEdit && !isEditable;

  // Fetch this category's record directly into local state. Shape-tolerant
  // (handles both { response: {...} } and a bare object) and cancel-safe.
  useEffect(() => {
    if (!isEdit || !id) {
      setRecord(null);
      return undefined;
    }
    let cancelled = false;
    setRecord(null);
    axiosInstance
      .get(`admin/categories/${id}`)
      .then((res) => {
        if (cancelled) return;
        const data = res?.data?.response ?? res?.data ?? null;
        setRecord(data && typeof data === "object" ? data : null);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isEdit, id]);

  // Banner master_id source data — only needed in edit mode.
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    (async () => {
      try {
        const listParams = {
          page: 1,
          search_key: "",
          status: "",
          type: "",
          per_page: 500,
        };
        const brandOccParams = {
          page: 1,
          search_key: "",
          status: "",
          per_page: 500,
        };
        const [catRes, occRes, brandRes] = await Promise.all([
          axiosInstance.get("admin/categories", { params: listParams }),
          axiosInstance.get("admin/occasions", { params: brandOccParams }),
          axiosInstance.get("admin/brands", { params: brandOccParams }),
        ]);
        if (cancelled) return;
        setCategoryOptions(
          (catRes.data?.response || []).map((it) => ({
            value: it.id,
            label: it.title,
          })),
        );
        setOccasionOptions(
          (occRes.data?.response || []).map((it) => ({
            value: it.id,
            label: it.name,
          })),
        );
        setBrandOptions(
          (brandRes.data?.response || []).map((it) => ({
            value: it.id,
            label: it.name,
          })),
        );
      } catch (err) {
        if (!cancelled)
          console.error("Banner option fetch failed:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit]);

  const initialValues = useMemo(() => {
    if (!isEdit) return INITIAL_VALUES;
    return {
      title: record?.title || "",
      ar_title: record?.ar_title || "",
      image: record?.image || "",
      ...landingPageValues(record?.landing_page),
      status: record?.status ?? CATEGORY_STATUS.ACTIVE,
      position: record?.position || 1,
      home_view: record?.home_view ?? true,
      banner_image: record?.banner_image || "",
      ar_banner_image: record?.ar_banner_image || "",
      banner_type: record?.banner_type || "",
      banner_master_id: record?.banner_master_id || "",
    };
  }, [isEdit, record]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => addcategary(), []),
    onSubmit: async (values) => {
      if (readOnly) return;
      setSubmitting(true);
      try {
        const categoryPayload = splitLandingPageValues(values);

        if (isEdit) {
          // PUT /admin/categories — updateCategory dispatches the actual call.
          // Allowed product types are managed on the Sub-category page now.
          dispatch(
            updateCategory(
              {
                ...categoryPayload,
                id: record?.id ?? Number(id),
                image: values.image,
                banner_image: emptyStrToNull(values.banner_image),
                ar_banner_image: emptyStrToNull(values.ar_banner_image),
                banner_type: normalizeBannerInt(values.banner_type),
                banner_master_id: normalizeBannerInt(values.banner_master_id),
              },
              setSubmitting,
              saveNav,
            ),
          );
        } else {
          // CREATE flow: POST the category.
          const response = await axiosInstance.post(
            "admin/categories",
            categoryPayload,
          );

          if (!response?.data?.status) {
            await swal({
              text: response?.data?.message || "Failed to create category",
              icon: "error",
              button: "Close",
            });
            setSubmitting(false);
            return;
          }

          await swal({
            text: response?.data?.response?.message || "Category created",
            icon: "success",
            button: "Close",
            timer: 1200,
          });

          setSubmitting(false);
          finishSave();
        }
      } catch (err) {
        await swal({
          text:
            err?.response?.data?.message ||
            err?.message ||
            "An error occurred while submitting the form",
          icon: "error",
          button: "Close",
        });
        setSubmitting(false);
      }
    },
  });

  const { values, errors, touched } = formik;
  const isDirty = formik.dirty && !submitting;
  const errorCount = Object.keys(errors).filter((k) => touched[k]).length;
  const imageUrl = values.image;
  const imageError = touched.image && errors.image;
  const isActive = Number(values.status) === CATEGORY_STATUS.ACTIVE;
  const visibleInHeader = Boolean(values.home_view);
  const previewName =
    (values.title || "").trim() ||
    (isEdit ? record?.title : "Category name") ||
    "Category name";
  const previewArName = (values.ar_title || "").trim();
  const previewSlug = slugify(values.title) || "category-slug";
  const landingPage = record?.landing_page || null;
  const previewMetaTitle =
    (values.lp_seo_title || "").trim() || `${previewName} | Nysmah`;
  const previewMetaDesc =
    (values.lp_meta_description || "").trim() || metaDescriptionFallback("en", previewName);
  const previewPath = pageAddress("en", landingPage, previewSlug);

  const bannerMasterOptions =
    Number(values.banner_type) === 1
      ? categoryOptions
      : Number(values.banner_type) === 2
      ? occasionOptions
      : Number(values.banner_type) === 3
      ? brandOptions
      : [];

  const processFile = async (file) => {
    if (!file || readOnly) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      formik.setFieldError("image", `Image must be under ${MAX_SIZE_MB}MB`);
      formik.setFieldTouched("image", true, false);
      return;
    }
    setUploading(true);
    try {
      await handleImageUpload(
        file,
        { title: "images", name: "image" },
        "image",
        formik,
      );
    } catch {
      formik.setFieldError("image", "Image upload failed. Try again.");
      formik.setFieldTouched("image", true, false);
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    e.target.value = "";
  };
  const onTileClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    processFile(e.dataTransfer.files?.[0]);
  };

  const onDiscard = () => {
    if (
      isDirty &&
      !window.confirm(
        t("You have unsaved changes. Leave this page and discard them?"),
      )
    ) {
      return;
    }
    finishCancel();
  };

  // Embedded (drawer) mode: Esc runs the same guarded discard as the back button.
  useEffect(() => {
    if (!embedded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDiscard();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    const errs = await formik.validateForm();
    formik.setTouched(
      Object.keys(errs).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    );
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus({ preventScroll: true });
      }
      return;
    }
    formik.handleSubmit(e);
  };

  const uploadBannerField = async (file, fieldName) => {
    if (!file || readOnly) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      formik.setFieldError(fieldName, `Image must be under ${MAX_SIZE_MB}MB`);
      return;
    }
    try {
      await handleImageUpload(
        file,
        { title: "images", name: fieldName },
        fieldName,
        formik,
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="nb-page">
      <UnsavedChangesGuard when={isDirty} />

      <div className="nb-topbar">
        {embedded ? (
          <button type="button" className="nb-topbar__back" onClick={onDiscard}>
            <ChevronLeft size={14} aria-hidden="true" />
            {t("Categories")}
          </button>
        ) : (
          <Link to="/category" className="nb-topbar__back">
            <ChevronLeft size={14} aria-hidden="true" />
            {t("Categories")}
          </Link>
        )}
        <ChevronRight size={12} className="nb-topbar__sep" aria-hidden="true" />
        <span className="nb-topbar__current">
          {isEdit ? record?.title || t("Edit category") : t("New category")}
        </span>
        {readOnly ? (
          <span
            className="nb-topbar__draft"
            style={{
              color: "var(--nb-muted)",
              background: "#f1f5f9",
              borderColor: "var(--nb-border-strong)",
            }}
          >
            <Lock size={11} aria-hidden="true" />
            {t("Read only")}
          </span>
        ) : (
          isDirty && (
            <span className="nb-topbar__draft">
              <span className="nb-topbar__dot" aria-hidden="true" />
              {isEdit ? t("Edited · unsaved") : t("Draft · unsaved")}
            </span>
          )
        )}
      </div>

      <form className="nb-shell" onSubmit={handleSubmit} noValidate>
        <div className="nb-form">
          <header className="nb-hero">
            <span className="nb-hero__eyebrow">
              <Sparkles size={12} aria-hidden="true" />
              {t("Catalog")}
            </span>
            <h1 className="nb-hero__title">
              {isEdit ? t("Edit category") : t("Create a new category")}
            </h1>
            <p className="nb-hero__sub">
              {t(
                "Categories organize your storefront — they appear in the main nav and group products under a single page.",
              )}
            </p>
          </header>

          {errorCount > 0 && (
            <div className="nb-banner" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>
                {errorCount === 1
                  ? t("1 field needs your attention.")
                  : t(`${errorCount} fields need your attention.`)}
              </span>
            </div>
          )}

          {/* 01 Identity */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">01</span>
              <div>
                <h2 className="nb-section__title">{t("Identity")}</h2>
                <p className="nb-section__hint">
                  {t("Bilingual name shown to shoppers across the storefront.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="title"
                  label={t("Category name (English)")}
                  required
                  touched={touched.title}
                  error={errors.title}
                >
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className="nb-input"
                    placeholder={t("e.g. Eau de Parfum")}
                    value={values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
                <FormField
                  name="ar_title"
                  label={t("Category name (Arabic)")}
                  required
                  touched={touched.ar_title}
                  error={errors.ar_title}
                >
                  <input
                    id="ar_title"
                    name="ar_title"
                    type="text"
                    className="nb-input"
                    placeholder={t("الاسم بالعربية")}
                    dir="rtl"
                    lang="ar"
                    value={values.ar_title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
              </div>
            </div>
          </section>

          {/* 02 Image */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">02</span>
              <div>
                <h2 className="nb-section__title">{t("Cover image")}</h2>
                <p className="nb-section__hint">
                  {t("Used on the storefront tile and category banner.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div
                className={`nb-drop ${dragOver ? "is-drag" : ""} ${
                  imageError ? "is-error" : ""
                } ${imageUrl ? "has-image" : ""}`}
                onClick={onTileClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTileClick();
                  }
                }}
                onDrop={onDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!readOnly) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                role="button"
                tabIndex={readOnly ? -1 : 0}
                aria-label={t("Upload category image")}
                aria-disabled={readOnly}
              >
                {imageUrl ? (
                  <>
                    <img src={imageUrl} alt={t("Category cover")} className="nb-drop__img" />
                    {!readOnly && (
                      <div className="nb-drop__overlay">
                        <Upload size={18} aria-hidden="true" />
                        <span>{t("Replace image")}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="nb-drop__empty">
                    <span className="nb-drop__bubble">
                      {uploading ? <Spinner /> : <ImageIcon size={20} aria-hidden="true" />}
                    </span>
                    <div className="nb-drop__title">
                      {uploading ? t("Uploading…") : t("Drop a cover image or click to upload")}
                    </div>
                    <div className="nb-drop__hint">
                      {t("PNG, JPG, WEBP or SVG · up to 5MB")}
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  id="image"
                  name="image"
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={onPickFile}
                  hidden
                  disabled={uploading || readOnly}
                />
              </div>
              {imageUrl && !readOnly && (
                <div className="nb-thumbrow">
                  <button type="button" className="nb-btn nb-btn--ghost nb-btn--sm" onClick={onTileClick}>
                    <Upload size={13} aria-hidden="true" />
                    {t("Replace")}
                  </button>
                  <button
                    type="button"
                    className="nb-btn nb-btn--danger-ghost nb-btn--sm"
                    onClick={() => formik.setFieldValue("image", "")}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                    {t("Remove")}
                  </button>
                </div>
              )}
              {imageError && (
                <p className="nb-fielderror" role="alert">
                  {t(errors.image)}
                </p>
              )}
            </div>
          </section>

          {/* 03 Banner — edit mode only */}
          {isEdit && (
            <section className={`nb-section nb-section--seo ${bannerOpen ? "is-open" : ""}`}>
              <div className="nb-section__head">
                <span className="nb-section__step">03</span>
                <div>
                  <h2 className="nb-section__title">
                    <ImageLucide size={14} aria-hidden="true" style={{ marginRight: 6, opacity: 0.7 }} />
                    {t("Hero banner")}
                    <span className="nb-section__optional">{t("Optional")}</span>
                  </h2>
                  <p className="nb-section__hint">
                    {t("Wide banner shown at the top of the category page.")}
                  </p>
                </div>
                <button
                  type="button"
                  className="nb-section__toggle"
                  onClick={() => setBannerOpen((o) => !o)}
                  aria-expanded={bannerOpen}
                  disabled={readOnly}
                >
                  {bannerOpen ? t("Hide") : (
                    <>
                      <Plus size={13} aria-hidden="true" />
                      {t("Add banner")}
                    </>
                  )}
                </button>
              </div>
              {bannerOpen && (
                <div className="nb-section__body">
                  <div className="nb-grid-2">
                    <FormField name="banner_image" label={t("Banner image")}>
                      <div
                        className={`nb-drop ${values.banner_image ? "has-image" : ""}`}
                        style={{ minHeight: 160, height: 160 }}
                        onClick={() => {
                          if (readOnly) return;
                          document.getElementById("banner_image_input")?.click();
                        }}
                        role="button"
                        tabIndex={readOnly ? -1 : 0}
                      >
                        {values.banner_image ? (
                          <img src={values.banner_image} alt="" className="nb-drop__img" />
                        ) : (
                          <div className="nb-drop__empty">
                            <span className="nb-drop__bubble">
                              <Upload size={16} aria-hidden="true" />
                            </span>
                            <div className="nb-drop__title">{t("Upload banner")}</div>
                          </div>
                        )}
                        <input
                          id="banner_image_input"
                          type="file"
                          accept={ACCEPTED_TYPES}
                          hidden
                          disabled={readOnly}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadBannerField(f, "banner_image");
                            e.target.value = "";
                          }}
                        />
                      </div>
                    </FormField>
                    <FormField
                      name="ar_banner_image"
                      label={t("Banner image (Arabic)")}
                    >
                      <div
                        className={`nb-drop ${values.ar_banner_image ? "has-image" : ""}`}
                        style={{ minHeight: 160, height: 160 }}
                        onClick={() => {
                          if (readOnly) return;
                          document.getElementById("ar_banner_image_input")?.click();
                        }}
                        role="button"
                        tabIndex={readOnly ? -1 : 0}
                      >
                        {values.ar_banner_image ? (
                          <img src={values.ar_banner_image} alt="" className="nb-drop__img" />
                        ) : (
                          <div className="nb-drop__empty">
                            <span className="nb-drop__bubble">
                              <Upload size={16} aria-hidden="true" />
                            </span>
                            <div className="nb-drop__title">{t("Upload AR banner")}</div>
                          </div>
                        )}
                        <input
                          id="ar_banner_image_input"
                          type="file"
                          accept={ACCEPTED_TYPES}
                          hidden
                          disabled={readOnly}
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) uploadBannerField(f, "ar_banner_image");
                            e.target.value = "";
                          }}
                        />
                      </div>
                    </FormField>
                    <FormField name="banner_type" label={t("Banner links to")}>
                      <ReactSelect
                        inputId="banner_type"
                        options={BANNER_TYPE_OPTIONS}
                        styles={nbSelectStyles}
                        placeholder={t("Pick a destination type…")}
                        isDisabled={readOnly}
                        value={
                          BANNER_TYPE_OPTIONS.find(
                            (o) => o.value === Number(values.banner_type),
                          ) || null
                        }
                        onChange={(opt) => {
                          formik.setFieldValue("banner_type", opt?.value || "");
                          formik.setFieldValue("banner_master_id", "");
                        }}
                        menuPortalTarget={document.body}
                      />
                    </FormField>
                    <FormField name="banner_master_id" label={t("Destination")}>
                      <ReactSelect
                        inputId="banner_master_id"
                        options={bannerMasterOptions}
                        styles={nbSelectStyles}
                        placeholder={t("Pick a destination…")}
                        isDisabled={readOnly || !values.banner_type}
                        value={
                          bannerMasterOptions.find(
                            (o) => o.value === values.banner_master_id,
                          ) || null
                        }
                        onChange={(opt) =>
                          formik.setFieldValue("banner_master_id", opt?.value || "")
                        }
                        menuPortalTarget={document.body}
                      />
                    </FormField>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* 05 Visibility */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">{isEdit ? "04" : "03"}</span>
              <div>
                <h2 className="nb-section__title">{t("Visibility")}</h2>
                <p className="nb-section__hint">
                  {t("Where this category appears, in what order, and whether it's live.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="status"
                  label={t("Status")}
                  touched={touched.status}
                  error={errors.status}
                >
                  <div className="nb-toggle" role="radiogroup" aria-label={t("Status")}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      className={`nb-toggle__opt ${isActive ? "is-on" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.ACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--on" />
                      {t("Active")}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!isActive}
                      className={`nb-toggle__opt ${!isActive ? "is-off" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.INACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--off" />
                      {t("Inactive")}
                    </button>
                  </div>
                </FormField>

                <FormField
                  name="position"
                  label={t("Position")}
                  hint={t("Lower numbers appear first.")}
                  touched={touched.position}
                  error={errors.position}
                >
                  <div className="nb-stepper">
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.max(POSITION_MIN, Number(values.position || 1) - 1),
                        )
                      }
                      aria-label={t("Decrease")}
                    >
                      −
                    </button>
                    <input
                      id="position"
                      name="position"
                      type="number"
                      inputMode="numeric"
                      min={POSITION_MIN}
                      max={POSITION_MAX}
                      className="nb-input nb-input--center"
                      value={values.position}
                      onChange={(e) => {
                        const v = e.target.value;
                        formik.setFieldValue("position", v === "" ? "" : Number(v));
                      }}
                      onBlur={formik.handleBlur}
                      disabled={readOnly}
                    />
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.min(POSITION_MAX, Number(values.position || 0) + 1),
                        )
                      }
                      aria-label={t("Increase")}
                    >
                      +
                    </button>
                  </div>
                </FormField>
              </div>

              <label className="nb-switchrow">
                <input
                  id="home_view"
                  name="home_view"
                  type="checkbox"
                  checked={visibleInHeader}
                  onChange={(e) =>
                    formik.setFieldValue("home_view", e.target.checked)
                  }
                  disabled={readOnly}
                />
                <span className="nb-switchrow__track" aria-hidden="true">
                  <span className="nb-switchrow__thumb" />
                </span>
                <span className="nb-switchrow__text">
                  <strong>{t("Show in main navigation")}</strong>
                  <span>
                    {t("Appears in the storefront top header along with other featured categories.")}
                  </span>
                </span>
              </label>
            </div>
          </section>

          {/* 06 SEO */}
          <LandingPageSeoSection
            formik={formik}
            readOnly={readOnly}
            step={isEdit ? "05" : "04"}
            kind="category"
            landingPage={landingPage}
            previewName={previewName}
            previewArName={previewArName}
          />
        </div>

        {/* Right: live preview */}
        <aside className="nb-preview">
          <div className="nb-preview__sticky">
            <div className="nb-preview__head">
              <span className="nb-preview__eyebrow">
                <span className="nb-preview__pulse" aria-hidden="true" />
                {t("Live preview")}
              </span>
              <div className="nb-preview__tabs" role="tablist">
                {PREVIEW_TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={previewTab === key}
                    className={`nb-preview__tab ${previewTab === key ? "is-active" : ""}`}
                    onClick={() => setPreviewTab(key)}
                  >
                    <Icon size={13} aria-hidden="true" />
                    {t(label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="nb-preview__stage">
              {previewTab === "storefront" && (
                <div className="nb-stage nb-stage--storefront">
                  <div className="nb-store-frame">
                    <div className="nb-store-frame__chrome">
                      <span /> <span /> <span />
                      <em>nysmah.com / category</em>
                    </div>
                    <div className="nb-store-card">
                      {visibleInHeader && (
                        <span className="nb-store-card__parent">
                          {t("Main nav")}
                        </span>
                      )}
                      <div className={`nb-store-card__media ${imageUrl ? "" : "is-empty"}`}>
                        {imageUrl ? (
                          <img src={imageUrl} alt={previewName} />
                        ) : (
                          <span className="nb-store-card__placeholder">
                            {previewName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="nb-store-card__body">
                        <h3 className="nb-store-card__name">{previewName}</h3>
                        {previewArName && (
                          <p className="nb-store-card__sub">{previewArName}</p>
                        )}
                        <span className="nb-store-card__cta">
                          {t("Shop the category")}
                          <ChevronRight size={13} aria-hidden="true" />
                        </span>
                      </div>
                      {!isActive && (
                        <span className="nb-store-card__hidden">
                          {t("Hidden — won't appear on storefront")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "page" && (
                <div className="nb-stage nb-stage--page">
                  <PagePreview
                    path={previewPath}
                    currentPath={landingPage?.path}
                    en={{
                      name: previewName,
                      heading: (values.lp_h1 || "").trim() || previewName,
                      html: values.lp_intro_html,
                    }}
                    ar={{
                      name: previewArName || previewName,
                      heading: (values.lp_ar_h1 || "").trim() || previewArName || previewName,
                      html: values.lp_ar_intro_html,
                    }}
                  />
                </div>
              )}
              {previewTab === "listing" && (
                <div className="nb-stage nb-stage--listing">
                  <div className="nb-listing-frame">
                    <div className="nb-listing-head">
                      <span>{t("Category")}</span>
                      <span>{t("Status")}</span>
                    </div>
                    <div className="nb-listing-row">
                      <div className="nb-listing-row__brand">
                        <div className={`nb-listing-avatar ${imageUrl ? "" : "is-empty"}`}>
                          {imageUrl ? (
                            <img src={imageUrl} alt={previewName} />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="nb-listing-row__info">
                          <strong>{previewName}</strong>
                          <span className="nb-listing-row__pos">
                            #{values.position || POSITION_MIN}
                          </span>
                        </div>
                      </div>
                      <div className="nb-listing-row__col">
                        <span
                          className={`nb-badge ${
                            isActive ? "nb-badge--on" : "nb-badge--off"
                          }`}
                        >
                          <span className="nb-badge__dot" aria-hidden="true" />
                          {isActive ? t("Active") : t("Inactive")}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "search" && (
                <div className="nb-stage nb-stage--search">
                  <div className="nb-search-frame">
                    <div className="nb-search-head">
                      <Search size={12} aria-hidden="true" />
                      <span>nysmah</span>
                    </div>
                    <div className="nb-search-result">
                      <div className="nb-search-result__url">
                        <span className="nb-search-result__favicon">
                          {imageUrl ? (
                            <img src={imageUrl} alt="" />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </span>
                        <div>
                          <span className="nb-search-result__site">nysmah.com</span>
                          <span className="nb-search-result__path">
                            {crumbPath(previewPath)}
                          </span>
                        </div>
                      </div>
                      <h4 className="nb-search-result__title">{previewMetaTitle}</h4>
                      <p className="nb-search-result__desc">{previewMetaDesc}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="nb-preview__caption">
              {previewTab === "storefront" && t("Tile shown on the category index.")}
              {previewTab === "page" && t("The page as the shopper sees it, with the score for Google.")}
              {previewTab === "listing" && t("Row shown in the admin Category List.")}
              {previewTab === "search" && t("Snippet on Google results — uses Meta fields.")}
            </div>
          </div>
        </aside>

        {!readOnly && (
          <div className={`nb-actionbar ${isDirty || submitting ? "is-visible" : ""}`}>
            <div className="nb-actionbar__inner">
              <span className="nb-actionbar__status">
                {submitting ? (
                  <>
                    <span className="nb-actionbar__spin" />
                    {isEdit ? t("Saving changes…") : t("Creating category…")}
                  </>
                ) : errorCount > 0 ? (
                  <>
                    <AlertCircle size={14} aria-hidden="true" />
                    {errorCount === 1
                      ? t("1 error to fix before saving")
                      : t(`${errorCount} errors to fix before saving`)}
                  </>
                ) : (
                  <>
                    <span className="nb-actionbar__dot" aria-hidden="true" />
                    {t("Unsaved changes")}
                  </>
                )}
              </span>
              <div className="nb-actionbar__btns">
                <button
                  type="button"
                  className="nb-btn nb-btn--ghost"
                  onClick={onDiscard}
                  disabled={submitting}
                >
                  {t("Discard")}
                </button>
                <button
                  type="submit"
                  className="nb-btn nb-btn--primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner />
                  ) : isEdit ? (
                    t("Save changes")
                  ) : (
                    t("Create category")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default CategoryForm;
